import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import type { ClientRecord } from "../../clients/clients.types";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import type {
  RoutingIssue,
  RoutingIssueKind,
  RoutingIssuesResponse,
} from "../meetingIntelligence.types";

import { sessionMark } from "stores/sessionGuard";
/**
 * Route ViewStore for Meeting Intelligence → **Routing Issues**.
 *
 * ── WHY THIS SCREEN EXISTS ─────────────────────────────────────────────────
 *
 * Approval was removed on 2026-09-18 (ruling R2 of
 * plan/2026-09-18_suggestions-and-ado-as-a-ticket-database.md), and the design
 * names the one thing that must not be lost with it:
 *
 *   "Dropping approval removes the only place a human saw an item before it
 *    reached a developer… an item whose assignee cannot be resolved, or that
 *    routes nowhere, still has to be visible to a human — that is a ROUTING
 *    problem, not an approval queue, and it keeps its own list."
 *
 * This is the web half of that list. It is deliberately NOT a queue: nothing on
 * it is waiting for somebody to say yes. Every row is a piece of PLUMBING that
 * is missing, and every row carries its own fix.
 *
 * ── THREE STATES THIS MUST NOT COLLAPSE ────────────────────────────────────
 *
 *   "nothing is wrong"          the healthy state, and a STRONG claim: every
 *                               distributed item reached a person and can file
 *                               an Azure DevOps work item.
 *   "HQ could not be reached"   we know nothing.
 *   "nothing has loaded yet"    we have not asked.
 *
 * `page`, `error` and `loaded` are tracked separately for exactly that, the
 * same way `AnalysisQueueStore` does it — an empty table and a failed request
 * must never render the same.
 *
 * Rule (web CLAUDE.md): route ViewStores are plain classes with
 * `makeAutoObservable` and do NOT extend ViewStore. The list is DERIVED on the
 * API (there is no `routing_issues` table), so `find` answers with a bare
 * report object rather than an entity page — it is held here rather than read
 * out of an entity cache, and the registration opts out of offline caching for
 * that reason (see serviceStores/meetingIntelligence.stores.ts).
 */
export default class RoutingIssuesStore {
  rootStore: RootStore;

  /** The last successful answer, or null. NEVER blanked by a failed refresh. */
  page: RoutingIssuesResponse | null = null;
  loading = false;
  /** Set when the load FAILED. Distinct from "no issues". */
  error: string | null = null;
  /** False until a load has completed once, successfully or not. */
  loaded = false;

  /** Server-side filters. Both are sent as query params. */
  clientId: string | null = null;
  kind: RoutingIssueKind | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /**
   * Resolved through the manager first, like the other derived-list stores — a
   * service path is not a property name on the root store, and reading it as
   * one silently yields undefined rather than failing.
   */
  private get issuesService(): ServiceStoreType | null {
    const root = this.rootStore as unknown as {
      serviceStoreManager?: { getStore: (p: string) => unknown };
    } & Record<string, unknown>;
    return (
      ((root.serviceStoreManager?.getStore("action-routing-issues") ??
        root["action-routing-issues"]) as ServiceStoreType | null) ?? null
    );
  }

  /** The `clients` ServiceStore — the client filter's option source. */
  private get clientsService(): ServiceStoreType | null {
    const root = this.rootStore as unknown as {
      serviceStoreManager?: { getStore: (p: string) => unknown };
      clientsStore?: unknown;
    };
    return (
      ((root.serviceStoreManager?.getStore("clients") ??
        root.clientsStore) as ServiceStoreType | null) ?? null
    );
  }

  // ---- Derived data -------------------------------------------------------

  /** The rows, already sorted worst-and-freshest-first by the API. */
  get issues(): RoutingIssue[] {
    return this.page?.issues ?? [];
  }

  /**
   * How many issues there are, or NULL when we do not know.
   *
   * Null is not zero. "Nothing is wrong" is the strongest claim this screen can
   * make and a failed request must never be allowed to make it.
   */
  get total(): number | null {
    return this.page ? this.page.total : null;
  }

  /** Declared-module gaps on active clients, or null when this answer carried none. */
  get moduleProfiles(): RoutingIssuesResponse["moduleProfiles"] | null {
    return this.page?.moduleProfiles ?? null;
  }

  /** Per-kind counts, or null before anything has loaded. */
  get counts(): Record<RoutingIssueKind, number> | null {
    return this.page?.counts ?? null;
  }

  /** The API's own sentence about what an empty list means. Rendered verbatim
   *  rather than restated, so the two halves cannot drift into disagreeing. */
  get note(): string | null {
    return this.page?.note ?? null;
  }

  /** True when a scan hit the server's cap, so `total` is a floor. */
  get truncated(): boolean {
    return this.page?.truncated === true;
  }

  /** True only when we KNOW there is nothing wrong. */
  get isHealthy(): boolean {
    return this.loaded && this.error === null && this.total === 0;
  }

  /** Client options for the filter (computed view over the `clients` cache). */
  get clients(): ClientRecord[] {
    return (this.clientsService?.entities ?? []) as unknown as ClientRecord[];
  }

  /** The filter's current client, for the picker's value. */
  get selectedClient(): ClientRecord | null {
    if (!this.clientId) return null;
    return this.clients.find((c) => c.id === this.clientId) ?? null;
  }

  // ---- Filters ------------------------------------------------------------

  setClientId(clientId: string | null) {
    this.clientId = clientId;
    void this.load();
  }

  setKind(kind: RoutingIssueKind | null) {
    this.kind = kind;
    void this.load();
  }

  // ---- Loading ------------------------------------------------------------

  async load(): Promise<void> {
    const service = this.issuesService;
    if (!service) {
      runInAction(() => {
        this.error =
          "The action-routing-issues service is not registered in this app.";
        this.loaded = true;
      });
      return;
    }
    const ended = sessionMark(this.rootStore);
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });
    try {
      const query: Record<string, unknown> = {};
      if (this.clientId) query.clientId = this.clientId;
      if (this.kind) query.kind = this.kind;
      // findOrThrow: the library ServiceStore resolves null on a failed find,
      // which left this screen on "Nothing has been loaded yet" with no reason.
      const res = (await findOrThrow(service, { query })) as unknown as
        | RoutingIssuesResponse
        | undefined;
      // Signed out meanwhile: the answer is the previous person's, and the
      // `loaded` latch below would make an emptied page read as a clean bill of
      // health (`isHealthy`).
      if (ended()) return;
      runInAction(() => {
        // A response with no `issues` array is not an answer — keep whatever we
        // last knew rather than reporting a clean bill of health we never got.
        if (res && Array.isArray(res.issues)) this.page = res;
      });
    } catch (err) {
      // `page` is left ALONE. Rendering "0 routing issues" because a request
      // failed is the one outcome this screen must never produce.
      if (ended()) return;
      runInAction(() => {
        this.error = loadErrorMessage(err, "Could not reach HQ's routing list.");
      });
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.loading = false;
          this.loaded = true;
        });
      }
    }
  }

  /** Load the client list that powers the filter. Non-fatal on failure — the
   *  picker then offers nothing and the unfiltered list still renders. */
  async loadClients(): Promise<void> {
    try {
      await this.clientsService?.find({ query: { $limit: 500 } });
    } catch (err) {
      console.error("Failed to load clients for the routing filter:", err);
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.page = null;
    this.loading = false;
    this.error = null;
    this.loaded = false;
    this.clientId = null;
    this.kind = null;
  }
}
